/**
 * Action Orchestrator - Unified system for orchestrating email and calendar actions
 * Supports parallel, sequential, and batched execution with human-like behavior simulation
 */

import { tracedEmailActions } from './tracedEmailActions';
import { unifiedCalendarService } from './calendar/unifiedCalendarService';

// Types
export interface Action {
  id: string;
  type: 'email' | 'calendar';
  operation: string;
  params: any;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dependencies?: string[]; // Action IDs that must complete first
  humanLike?: boolean; // Whether to simulate human-like timing
}

export interface OrchestrationResult {
  actionId: string;
  success: boolean;
  result?: any;
  error?: string;
  duration: number;
  timestamp: string;
}

export interface OrchestrationPlan {
  actions: Action[];
  executionMode: 'parallel' | 'sequential' | 'smart'; // smart = auto-detect dependencies
  humanSimulation?: {
    enabled: boolean;
    baseDelay?: number; // milliseconds
    variability?: number; // percentage variance
    readingTime?: boolean; // simulate reading time for emails
  };
}

// Human-like behavior simulation
class HumanBehaviorSimulator {
  private baseDelay = 1000; // 1 second base delay
  private variability = 0.3; // 30% variance
  
  /**
   * Calculate human-like delay based on action type and content
   */
  async simulateDelay(action: Action): Promise<void> {
    if (!action.humanLike) return;
    
    let delay = this.baseDelay;
    
    // Adjust delay based on action type
    switch (action.operation) {
      case 'search':
        delay = this.randomize(500, 1500); // Quick search
        break;
      case 'read':
        delay = this.calculateReadingTime(action.params);
        break;
      case 'compose':
      case 'reply':
        delay = this.calculateTypingTime(action.params);
        break;
      case 'send':
        delay = this.randomize(300, 800); // Quick send action
        break;
      case 'delete':
      case 'archive':
        delay = this.randomize(200, 500); // Quick actions
        break;
      case 'schedule':
      case 'createEvent':
        delay = this.randomize(1000, 3000); // Thoughtful scheduling
        break;
      default:
        delay = this.randomize(500, 1500);
    }
    
    // Add natural variability
    delay = delay * (1 + (Math.random() - 0.5) * this.variability);
    
    await this.sleep(delay);
  }
  
  /**
   * Calculate reading time based on email content length
   */
  private calculateReadingTime(params: any): number {
    const content = params.body || params.content || '';
    const wordCount = content.split(/\s+/).length;
    const wordsPerMinute = 200; // Average reading speed
    const readingTimeMs = (wordCount / wordsPerMinute) * 60 * 1000;
    
    // Min 2 seconds, max 10 seconds for simulation
    return Math.min(Math.max(readingTimeMs, 2000), 10000);
  }
  
  /**
   * Calculate typing time based on content length
   */
  private calculateTypingTime(params: any): number {
    const content = params.body || params.content || '';
    const charCount = content.length;
    const charsPerMinute = 200; // Average typing speed (40 WPM)
    const typingTimeMs = (charCount / charsPerMinute) * 60 * 1000;
    
    // Min 3 seconds, max 15 seconds for simulation
    return Math.min(Math.max(typingTimeMs, 3000), 15000);
  }
  
  /**
   * Generate randomized delay within range
   */
  private randomize(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }
  
  /**
   * Sleep for specified duration
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Action Orchestrator
export class ActionOrchestrator {
  private humanSimulator = new HumanBehaviorSimulator();
  private executionHistory: OrchestrationResult[] = [];
  
  /**
   * Execute a plan of actions with orchestration
   */
  async executePlan(plan: OrchestrationPlan): Promise<OrchestrationResult[]> {
    console.log(`[Orchestrator] Executing plan with ${plan.actions.length} actions in ${plan.executionMode} mode`);
    
    // Apply human simulation settings to actions
    if (plan.humanSimulation?.enabled) {
      plan.actions.forEach(action => {
        action.humanLike = true;
      });
    }
    
    let results: OrchestrationResult[] = [];
    
    switch (plan.executionMode) {
      case 'parallel':
        results = await this.executeParallel(plan.actions);
        break;
      case 'sequential':
        results = await this.executeSequential(plan.actions);
        break;
      case 'smart':
        results = await this.executeSmart(plan.actions);
        break;
    }
    
    this.executionHistory.push(...results);
    return results;
  }
  
  /**
   * Execute actions in parallel (all at once)
   */
  private async executeParallel(actions: Action[]): Promise<OrchestrationResult[]> {
    console.log(`[Orchestrator] Executing ${actions.length} actions in parallel`);
    
    const promises = actions.map(action => this.executeAction(action));
    return await Promise.all(promises);
  }
  
  /**
   * Execute actions sequentially (one after another)
   */
  private async executeSequential(actions: Action[]): Promise<OrchestrationResult[]> {
    console.log(`[Orchestrator] Executing ${actions.length} actions sequentially`);
    
    const results: OrchestrationResult[] = [];
    
    for (const action of actions) {
      const result = await this.executeAction(action);
      results.push(result);
      
      // Stop on critical failures
      if (!result.success && action.priority === 'urgent') {
        console.warn(`[Orchestrator] Critical action ${action.id} failed, stopping execution`);
        break;
      }
    }
    
    return results;
  }
  
  /**
   * Execute actions intelligently based on dependencies and priorities
   */
  private async executeSmart(actions: Action[]): Promise<OrchestrationResult[]> {
    console.log(`[Orchestrator] Executing ${actions.length} actions with smart orchestration`);
    
    // Build dependency graph
    const graph = this.buildDependencyGraph(actions);
    const results: OrchestrationResult[] = [];
    const completed = new Set<string>();
    
    while (completed.size < actions.length) {
      // Find actions that can be executed (no pending dependencies)
      const ready = actions.filter(action => {
        if (completed.has(action.id)) return false;
        if (!action.dependencies) return true;
        return action.dependencies.every(dep => completed.has(dep));
      });
      
      if (ready.length === 0) {
        console.error('[Orchestrator] Circular dependency detected or all actions failed');
        break;
      }
      
      // Execute ready actions in parallel
      const batchResults = await this.executeParallel(ready);
      results.push(...batchResults);
      
      // Mark completed
      batchResults.forEach(result => {
        if (result.success) {
          completed.add(result.actionId);
        }
      });
    }
    
    return results;
  }
  
  /**
   * Build dependency graph for smart execution
   */
  private buildDependencyGraph(actions: Action[]): Map<string, Set<string>> {
    const graph = new Map<string, Set<string>>();
    
    actions.forEach(action => {
      if (action.dependencies) {
        graph.set(action.id, new Set(action.dependencies));
      } else {
        graph.set(action.id, new Set());
      }
    });
    
    return graph;
  }
  
  /**
   * Execute a single action
   */
  private async executeAction(action: Action): Promise<OrchestrationResult> {
    const startTime = Date.now();
    
    try {
      // Simulate human-like delay before action
      await this.humanSimulator.simulateDelay(action);
      
      let result: any;
      
      if (action.type === 'email') {
        result = await this.executeEmailAction(action);
      } else if (action.type === 'calendar') {
        result = await this.executeCalendarAction(action);
      } else {
        throw new Error(`Unknown action type: ${action.type}`);
      }
      
      const duration = Date.now() - startTime;
      
      return {
        actionId: action.id,
        success: true,
        result,
        duration,
        timestamp: new Date().toISOString()
      };
    } catch (error: any) {
      const duration = Date.now() - startTime;
      
      return {
        actionId: action.id,
        success: false,
        error: error.message,
        duration,
        timestamp: new Date().toISOString()
      };
    }
  }
  
  /**
   * Execute email action
   */
  private async executeEmailAction(action: Action): Promise<any> {
    const { operation, params } = action;
    
    console.log(`[Orchestrator] Executing email action: ${operation}`);
    
    switch (operation) {
      case 'search':
        return await tracedEmailActions.searchEmails(params);
      case 'send':
        return await tracedEmailActions.sendEmail(params);
      case 'reply':
        return await tracedEmailActions.replyToEmail(params.messageId, params);
      case 'forward':
        return await tracedEmailActions.forwardEmail(params.messageId, params);
      case 'delete':
        return await tracedEmailActions.deleteEmail(params.messageId);
      case 'archive':
        return await tracedEmailActions.archiveEmail(params.messageId);
      case 'markAsRead':
        return await tracedEmailActions.markAsRead(params.messageId);
      case 'markAsUnread':
        return await tracedEmailActions.markAsUnread(params.messageId);
      case 'addLabel':
        return await tracedEmailActions.addLabel(params.messageId, params.label);
      case 'removeLabel':
        return await tracedEmailActions.removeLabel(params.messageId, params.label);
      case 'createDraft':
        return await tracedEmailActions.createDraft(params);
      case 'updateDraft':
        return await tracedEmailActions.updateDraft(params.draftId, params);
      case 'getRecentEmails':
        return await tracedEmailActions.getRecentEmails(params);
      default:
        throw new Error(`Unknown email operation: ${operation}`);
    }
  }
  
  /**
   * Execute calendar action
   */
  private async executeCalendarAction(action: Action): Promise<any> {
    const { operation, params } = action;
    
    console.log(`[Orchestrator] Executing calendar action: ${operation}`);
    
    switch (operation) {
      case 'searchEvents':
        return await unifiedCalendarService.searchEvents(params.query, params.options);
      case 'createEvent':
        return await unifiedCalendarService.createEvent(params);
      case 'updateEvent':
        return await unifiedCalendarService.updateEvent(params.eventId, params.updates);
      case 'deleteEvent':
        return await unifiedCalendarService.deleteEvent(params.eventId);
      case 'getUpcomingEvents':
        return await unifiedCalendarService.getUpcomingEvents(params);
      case 'getEventsForDay':
        return await unifiedCalendarService.getEventsForDay(params.date);
      case 'getEventsForWeek':
        return await unifiedCalendarService.getEventsForWeek(params.date);
      case 'getEventsForMonth':
        return await unifiedCalendarService.getEventsForMonth(params.date);
      case 'findAvailableSlots':
        return await unifiedCalendarService.findAvailableSlots(params);
      default:
        throw new Error(`Unknown calendar operation: ${operation}`);
    }
  }
  
  /**
   * Get execution history
   */
  getExecutionHistory(): OrchestrationResult[] {
    return [...this.executionHistory];
  }
  
  /**
   * Clear execution history
   */
  clearHistory(): void {
    this.executionHistory = [];
  }
  
  /**
   * Get statistics about executions
   */
  getStats() {
    const total = this.executionHistory.length;
    const successful = this.executionHistory.filter(r => r.success).length;
    const failed = total - successful;
    const avgDuration = total > 0 
      ? this.executionHistory.reduce((sum, r) => sum + r.duration, 0) / total 
      : 0;
    
    return {
      total,
      successful,
      failed,
      successRate: total > 0 ? (successful / total) * 100 : 0,
      avgDuration: Math.round(avgDuration),
    };
  }
}

// Singleton instance
export const actionOrchestrator = new ActionOrchestrator();

// Helper function to create common action plans
export const createActionPlan = {
  /**
   * Send multiple emails in parallel
   */
  sendBulkEmails(emails: any[]): OrchestrationPlan {
    return {
      actions: emails.map((email, i) => ({
        id: `send-email-${i}`,
        type: 'email',
        operation: 'send',
        params: email,
        humanLike: true,
      })),
      executionMode: 'parallel',
      humanSimulation: { enabled: true },
    };
  },
  
  /**
   * Process inbox workflow (search -> read -> reply)
   */
  processInbox(query: string, replyTemplate: any): OrchestrationPlan {
    return {
      actions: [
        {
          id: 'search',
          type: 'email',
          operation: 'search',
          params: { query },
          humanLike: true,
        },
        {
          id: 'reply',
          type: 'email',
          operation: 'reply',
          params: replyTemplate,
          dependencies: ['search'],
          humanLike: true,
        },
      ],
      executionMode: 'smart',
      humanSimulation: { enabled: true },
    };
  },
  
  /**
   * Schedule meeting with email confirmation
   */
  scheduleWithConfirmation(eventData: any, emailData: any): OrchestrationPlan {
    return {
      actions: [
        {
          id: 'create-event',
          type: 'calendar',
          operation: 'createEvent',
          params: eventData,
          humanLike: true,
        },
        {
          id: 'send-confirmation',
          type: 'email',
          operation: 'send',
          params: emailData,
          dependencies: ['create-event'],
          humanLike: true,
        },
      ],
      executionMode: 'smart',
      humanSimulation: { enabled: true },
    };
  },
};
